import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import { ArrowBack, Add, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import retentionService from '../services/retentionService';
import ProceedingModalForm from '../../proceedings/components/ProceedingModalForm';
import proceedingService from '../../proceedings/services/proceedingService';
import companyService from '../../companies/services/companyService';
import { useAuth } from '../../../hooks/useAuth';
import useSortTable from '../../../hooks/useSortTable';
import PageMasthead from '../../../components/PageMasthead';

const RetentionLineDetail = () => {
  const { lineId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [line, setLine] = useState(null);
  const [proceedings, setProceedings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [showProceedingForm, setShowProceedingForm] = useState(false);
  const [editProceeding, setEditProceeding] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [retentions, setRetentions] = useState([]);

  const { sorted, sortBy, sortDir, handleSort } = useSortTable(proceedings);

  const SortCell = ({ label, field }) => (
    <TableCell onClick={() => handleSort(field)} sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
      <TableSortLabel active={sortBy === field} direction={sortBy === field ? sortDir : 'asc'}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  useEffect(() => {
    loadLineDetail();
    loadProceedings();
  }, [lineId]);

  const loadLineDetail = async () => {
    try {
      setLoading(true);
      const response = await retentionService.getLineById(lineId);
      setLine(response.data);
    } catch (error) {
      console.error('Error loading retention line:', error);
      setError('Error al cargar la linea de retencion');
    } finally {
      setLoading(false);
    }
  };

  const loadProceedings = async () => {
    try {
      const response = await retentionService.getLineProceedings(lineId);
      setProceedings(response.data || []);
    } catch (error) {
      console.error('Error loading proceedings:', error);
    }
  };

  const loadCompaniesAndRetentions = async () => {
    try {
      const companiesResponse = await companyService.getAll();
      setCompanies(companiesResponse.data || []);
      const companyId = user?.companyId || '';
      const retentionsResponse = await retentionService.getAll({ companyId });
      setRetentions(retentionsResponse.data || []);
    } catch (error) {
      console.error('Error loading companies and retentions:', error);
    }
  };

  const handleCreateProceeding = async () => {
    setEditProceeding(null);
    await loadCompaniesAndRetentions();
    setShowProceedingForm(true);
  };

  const handleEditProceeding = (proceeding) => {
    setEditProceeding(proceeding);
    loadCompaniesAndRetentions();
    setShowProceedingForm(true);
  };

  const handleDeleteProceeding = async (proceedingId) => {
    if (!window.confirm('¿Eliminar este expediente?')) return;
    try {
      await proceedingService.delete(proceedingId);
      loadProceedings();
    } catch (error) {
      console.error('Error deleting proceeding:', error);
    }
  };

  const handleSaveProceeding = async (values) => {
    try {
      if (editProceeding) {
        await proceedingService.update(editProceeding.id, values);
      } else {
        await proceedingService.create(values);
      }
      setShowProceedingForm(false);
      setEditProceeding(null);
      loadProceedings();
    } catch (error) {
      throw error;
    }
  };

  const getDispositionText = () => {
    if (!line) return '\u2014';
    const items = [];
    if (line.dispositionCt) items.push('Conservacion total');
    if (line.dispositionE) items.push('Eliminacion');
    if (line.dispositionM) items.push('Microfilmacion');
    if (line.dispositionD) items.push('Digitalizacion');
    if (line.dispositionS) items.push('Seleccion');
    return items.length > 0 ? items.join(', ') : '\u2014';
  };

  const formatDate = (date) => {
    if (!date) return '\u2014';
    return new Date(date).toLocaleDateString('es-ES', { timeZone: 'UTC' });
  };

  const InfoField = ({ label, children }) => (
    <Box>
      <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(29,31,32,0.55)', mb: 0.5 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Box>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !line) {
    return (
      <Box>
        <Alert severity="error">{error || 'Linea de retencion no encontrada'}</Alert>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }} startIcon={<ArrowBack />}>
          Volver
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Masthead */}
      <PageMasthead
        title={`${line.series} / ${line.subseries || ''}`}
        subtitle={`Linea de retencion · ${line.code}`}
      />

      {/* Back */}
      <Box sx={{ mt: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/retentions/${line.retention?.id || ''}`)}
          sx={{ color: '#5980a6', fontWeight: 700, fontSize: '0.9375rem' }}
        >
          {line.retention?.name || 'Retencion'}
        </Button>
      </Box>

      {/* Info card */}
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: '20px', mb: 3 }}>
          <InfoField label="Serie">
            <Typography variant="body1">{line.series}</Typography>
          </InfoField>
          <InfoField label="Subserie">
            <Typography variant="body1">{line.subseries || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Codigo">
            <Typography variant="body1">{line.code}</Typography>
          </InfoField>
          <InfoField label="Tabla de Retencion">
            <Typography variant="body1">{line.retention?.name || '\u2014'}</Typography>
          </InfoField>
          <InfoField label="Documentos">
            <Typography variant="body1">{line.documents || '\u2014'}</Typography>
          </InfoField>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: '20px' }}>
          <InfoField label="Retencion Local">
            <Typography variant="body1">{line.localRetention} anos</Typography>
          </InfoField>
          <InfoField label="Retencion Central">
            <Typography variant="body1">{line.centralRetention} anos</Typography>
          </InfoField>
          <InfoField label="Disposicion Final">
            <Typography variant="body1">{getDispositionText()}</Typography>
          </InfoField>
          {line.procedure && (
            <InfoField label="Procedimiento" >
              <Typography variant="body1">{line.procedure}</Typography>
            </InfoField>
          )}
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ mb: 3, display: 'inline-flex' }}>
        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)}>
          <Tab label="Expedientes" />
        </Tabs>
      </Box>

      {/* Tab: Expedientes */}
      {tabIndex === 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Expedientes ({proceedings.length})
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={handleCreateProceeding}>
              Nuevo Expediente
            </Button>
          </Box>

          <Paper sx={{ overflow: 'hidden' }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <SortCell label="Codigo" field="code" />
                    <SortCell label="Nombre" field="name" />
                    <SortCell label="Fecha Inicio" field="startDate" />
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Estado</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sorted.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No hay expedientes asociados a esta linea de retencion
                      </TableCell>
                    </TableRow>
                  ) : (
                    sorted.map((proceeding) => (
                      <TableRow
                        key={proceeding.id}
                        hover
                        onClick={() => navigate(`/proceedings/${proceeding.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: '"Barlow Condensed", system-ui, sans-serif', fontWeight: 600, fontStyle: 'italic' }}>
                            {proceeding.code}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{proceeding.name}</Typography></TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{formatDate(proceeding.startDate)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={proceeding.endDate ? 'Cerrado' : 'Activo'}
                            size="small"
                            variant="outlined"
                            sx={{ borderColor: 'rgba(29,31,32,0.16)', fontSize: '0.75rem' }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => handleEditProceeding(proceeding)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <EditOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" onClick={() => handleDeleteProceeding(proceeding.id)} sx={{ color: '#5d5d60', width: 36, height: 36 }}>
                                <DeleteOutlined sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {showProceedingForm && (
        <ProceedingModalForm
          open={showProceedingForm}
          onClose={() => { setShowProceedingForm(false); setEditProceeding(null); }}
          onSave={handleSaveProceeding}
          proceeding={editProceeding || {
            retentionLineId: lineId,
            retentionLine: { retentionId: line?.retention?.id },
            companyId: user?.companyId || '',
          }}
          companies={companies}
          retentions={retentions}
          onCompanyChange={async (companyId) => {
            try {
              const response = await retentionService.getAll({ companyId });
              setRetentions(response.data || []);
            } catch (error) {
              console.error('Error loading retentions:', error);
            }
          }}
        />
      )}
    </Box>
  );
};

export default RetentionLineDetail;
